import React from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import spacing from '../spacing';

/**
 * Stack Component
 * @component
 * @description Flexible layout component for vertical or horizontal stacking with consistent spacing
 * 
 * @example
 * // Vertical stack
 * <Stack spacing={3}>
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 * </Stack>
 * 
 * @example
 * // Horizontal stack
 * <Stack direction="row" spacing={2} align="center">
 *   <Button>Action 1</Button>
 *   <Button>Action 2</Button>
 * </Stack>
 */

const StyledStack = styled(Box, {
  shouldForwardProp: (prop) => !['direction', 'spacing', 'align', 'justify', 'wrap', 'divider'].includes(prop),
})(({
  direction = 'column',
  spacing: spacingProp = 2,
  align,
  justify,
  wrap,
  divider,
}) => {
  const isRow = direction === 'row' || direction === 'row-reverse';
  const spacingValue = typeof spacingProp === 'number' 
    ? spacing[spacingProp] 
    : spacingProp;

  return {
    display: 'flex',
    flexDirection: direction,
    
    ...(align && {
      alignItems: align,
    }),
    
    ...(justify && {
      justifyContent: justify,
    }),
    
    ...(wrap && {
      flexWrap: 'wrap',
    }),
    
    // Add spacing between children
    '& > *:not(:last-child)': {
      ...(isRow ? {
        marginRight: spacingValue,
      } : {
        marginBottom: spacingValue,
      }),
    },
    
    // Divider styling
    ...(divider && {
      '& > *:not(:last-child)': {
        ...(isRow ? {
          borderRight: divider,
          paddingRight: spacingValue,
        } : {
          borderBottom: divider,
          paddingBottom: spacingValue,
        }),
      },
    }),
  };
});

const Stack = ({
  children,
  direction = 'column',
  spacing: spacingProp = 2,
  align,
  justify,
  wrap = false,
  divider,
  component = 'div',
  ...props
}) => {
  return (
    <StyledStack
      as={component}
      direction={direction}
      spacing={spacingProp}
      align={align}
      justify={justify}
      wrap={wrap}
      divider={divider}
      {...props}
    >
      {children}
    </StyledStack>
  );
};

export default Stack;
